import type { components } from "../generated/api";

/**
 * Ошибка REST-контракта.
 *
 * `errors` типизирован СГЕНЕРИРОВАННЫМ типом, а не переписанным вручную:
 * обязательность полей задаёт схема и меняет её бэк — с круга 9 (2026-09-03)
 * `key`, `message` и `code` обязательны, `path` нет. Держать это знание в
 * рукописном типе значит гарантированно разойтись со схемой: прежняя редакция
 * этого докблока утверждала «все четыре поля необязательны» и пережила правку.
 */
export type ApiError = components["schemas"]["Error"];

const MALFORMED_BODY_KEY = "transport.malformedBody";

export class RestomenuError extends Error {
  readonly status: number;
  readonly errors: readonly ApiError[];
  /**
   * Секунды из заголовка `Retry-After` (429, 503); null — заголовка нет или
   * он не в секундах. В браузере заголовок виден, только если API отдаёт его
   * в `Access-Control-Expose-Headers`, — без этого здесь всегда null.
   */
  readonly retryAfter: number | null;

  constructor(
    status: number,
    errors: readonly ApiError[],
    message: string,
    retryAfter: number | null = null,
  ) {
    super(message);
    this.name = "RestomenuError";
    this.status = status;
    this.errors = errors;
    this.retryAfter = retryAfter;
  }

  /** Сессии нет, токен негоден или истёк. 401 всегда означает именно это. */
  get isUnauthenticated(): boolean {
    return this.status === 401;
  }

  get isForbidden(): boolean {
    return this.status === 403;
  }

  /**
   * Хотя бы одна ошибка выдана транспортом при разборе запроса, до входа
   * в домен. Значит чинить надо сам запрос, а не состояние.
   */
  get isTransport(): boolean {
    return this.errors.some((e) => e.code === 0);
  }

  has(key: string): boolean {
    return this.errors.some((e) => e.key === key);
  }

  /** Сообщение для конкретного поля входа. Путь — массив: сегмент может содержать точку. */
  at(path: readonly string[]): string | undefined {
    const hit = this.errors.find(
      (e) => Array.isArray(e.path) && e.path.length === path.length && e.path.every((s, i) => s === path[i]),
    );
    return hit?.message;
  }
}

/**
 * Проверяем не только «это объект», но и что `errors` — действительно массив.
 * Схема ответа закрыта, но обработчик ошибок обязан переживать ЛЮБОЕ тело:
 * если сюда прилетит `{errors: "oops"}` (прокси, заглушка, чужой сервис),
 * то без этой проверки `.some()` бросит внутри разбора ошибки — и вместо
 * причины наружу уедет TypeError из самого обработчика.
 */
function isApiErrorList(value: unknown): value is { errors?: ApiError[] } {
  if (typeof value !== "object" || value === null || Array.isArray(value)) return false;
  const { errors } = value as { errors?: unknown };
  return errors === undefined || Array.isArray(errors);
}

/**
 * Собрать RestomenuError из ответа и уже прочитанного тела.
 *
 * Тело читает вызывающий: только он знает, был ли content-type разбираемым.
 * Строка здесь означает «прочитали как текст, JSON не получился».
 */
export function parseErrorResponse(response: Response, body: unknown): RestomenuError {
  const { status } = response;
  // Читается ДО ветки 5xx: срок нужен и 503, чьё тело ниже отбрасывается.
  const retryAfter = parseRetryAfter(response.headers.get("Retry-After"));

  // 5xx формирует фреймворк: наружу уходит HTML-страница Symfony, не JSON.
  if (status >= 500) {
    return new RestomenuError(status, [], `Сервер ответил ${status}`, retryAfter);
  }

  if (typeof body === "string") {
    return new RestomenuError(
      status,
      [{ key: MALFORMED_BODY_KEY, message: "Тело ответа не разобрано", code: 0 }],
      `Тело ответа ${status} не разобрано`,
      retryAfter,
    );
  }

  // Тело отказа гейта — Empty, то есть {}. Причина наружу не утекает намеренно.
  const errors: readonly ApiError[] = isApiErrorList(body) ? (body.errors ?? []) : [];

  const first = errors[0];
  const message = first?.message ?? first?.key ?? `Запрос отклонён со статусом ${status}`;

  return new RestomenuError(status, errors, message, retryAfter);
}

/**
 * `Retry-After` в секундах — целое неотрицательное. Форму HTTP-даты бэк не
 * шлёт (`components/headers/RetryAfter`), её и прочий мусор — в null.
 */
export function parseRetryAfter(value: string | null): number | null {
  if (value === null || !/^\d+$/.test(value)) return null;
  return Number(value);
}

/**
 * Мост между родной формой openapi-fetch и объектом ошибки.
 *
 * `error` у openapi-fetch — это РАЗОБРАННЫЙ JSON, а не экземпляр класса:
 * подменить его в middleware на RestomenuError нельзя, он не переживёт
 * прохождение через Response. Поэтому превращение — отдельная чистая
 * функция: её зовёт query.ts перед throw, и её же зовут саги Ф3.
 */
export function toRestomenuError(error: unknown, response: Response): RestomenuError {
  // Пустая строка — тело без байтов (гейт, Content-Length: 0), не битый JSON.
  return parseErrorResponse(response, error === "" ? {} : (error ?? {}));
}
